import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supportedLocales, type SupportedLocale } from '@org/i18n';
import { trpc } from '@org/clients/school';
import { useAuth } from './components/login/auth-context';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { DEFAULT_LOCALE, isSupportedLocale, resolveInitialLocale } from './locale-default';

type LocaleContextValue = {
  locale: SupportedLocale;
  userLocale: SupportedLocale;
  availableLocales: SupportedLocale[];
  toggleLocale: () => void;
  setLocale: (locale: SupportedLocale) => void;
  isReady: boolean;
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const email = user?.email ?? '';
  const { i18n } = useTranslation();
  const { data: profile, isLoading } = trpc.users.getUserByEmail.useQuery(email, {
    enabled: Boolean(email),
  });
  const { mutate: persistUiLanguage } = trpc.users.setUiLanguage.useMutation();
  const [userLocale, setUserLocale] = useState<SupportedLocale>(DEFAULT_LOCALE);
  const [locale, setLocaleState] = useState<SupportedLocale>(DEFAULT_LOCALE);
  const [initialized, setInitialized] = useState(false);

  // First open: pick the language once the profile is known (or known to be
  // missing). A toggle press afterwards is remembered on the user's row, so
  // the next visit — on any device — starts from that choice.
  useEffect(() => {
    if (initialized) return;
    if (!profile && isLoading) return;
    const initial = resolveInitialLocale(profile);
    setLocaleState(initial);
    void i18n.changeLanguage(initial);
    setInitialized(true);
  }, [profile, initialized, i18n, isLoading]);

  useEffect(() => {
    const profileLocale = profile?.language;
    if (isSupportedLocale(profileLocale)) setUserLocale(profileLocale);
  }, [profile?.language]);

  const changeLocale = useCallback(
    (nextLocale: SupportedLocale) => {
      setLocaleState(nextLocale);
      void i18n.changeLanguage(nextLocale);
      if (profile) persistUiLanguage({ language: nextLocale });
    },
    [i18n, profile, persistUiLanguage],
  );

  const toggleLocale = useCallback(() => {
    changeLocale(locale === 'en' ? 'pt' : 'en');
  }, [changeLocale, locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      userLocale,
      availableLocales: supportedLocales,
      toggleLocale,
      setLocale: changeLocale,
      isReady: initialized && i18n.isInitialized,
    }),
    [changeLocale, initialized, i18n, locale, toggleLocale, userLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    const message =
      i18next.t('shared.locale.errors.missingProvider', {
        defaultValue: 'useLocale must be used within a LocaleProvider.',
      }) ?? 'useLocale must be used within a LocaleProvider.';
    throw new Error(message);
  }
  return context;
};

export default LocaleProvider;
